import React, { useState, useEffect, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import { API, graphqlOperation } from "aws-amplify";
import { getMCCTaskDeleteChannelsDetails } from "graphql/queries.js";

// react-bootstrap components
import { Container, Row, Col, Spinner, Card } from "react-bootstrap";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination";
import { getTaskStatusLabel } from "utilities/uiMappingUtilities";

function DeleteChannelsTaskDetails() {
  const { cloudTaskId } = useParams();
  const history = useHistory();
  const notify = useContext(NotificationAlertContext);
  const [taskData, setTaskData] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);

  // Table Config Starts
  const headers = [
    { key: "channel_id", label: "Channel ID" },
    { key: "channel_name", label: "Channel Name" },
  ];
  const columnMapping = {
    "Channel ID": "channel_id",
    "Channel Name": "channel_name",
  };
  // Table Config Ends

  const outcomeOptions = [
    { value: 0, label: "Success" },
    { value: 1, label: "Failure" },
  ];

  const getOutcomeLabel = (value) =>
    outcomeOptions.find((o) => o.value === value)?.label ?? value ?? "-";

  // Fetch task details using GraphQL
  const fetchDeleteChannelsTaskDetails = async () => {
    try {
      setLoading(true);

      const response = await API.graphql(
        graphqlOperation(getMCCTaskDeleteChannelsDetails, {
          cloud_task_id: cloudTaskId,
        }),
      );

      const result = response?.data?.getMCCTaskDeleteChannelsDetails;

      if (result?.success) {
        const body = result.body;
        setTaskData(body);

        // Set report data from the details array
        if (body?.details && Array.isArray(body.details)) {
          setReportData(body.details);
        } else {
          setReportData([]);
        }

        // Set message from body
        setMessage(body?.message || body?.error || "");

        notify("success", "Task details loaded successfully");
        return true;
      } else {
        throw new Error(result.message || "Failed to fetch task details");
      }
    } catch (error) {
      console.error("Error fetching task details:", error);
      history.push("/admin/mirth-tasks");
      notify("danger", `Failed to load task details: ${error.message}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      if (cloudTaskId) {
        const isSuccess = await fetchDeleteChannelsTaskDetails();
        if (!isSuccess) {
          notify("danger");
        }
      } else {
        notify("danger", "Missing required parameters for task details");
        history.push("/admin/mirth-tasks");
      }
    }
    fetchData();
  }, [cloudTaskId]);

  if (loading) return <Spinner animation="grow" />;

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <h2>Cloud Task - Delete Channels Details</h2>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <Card>
              <Card.Body>
                <Row>
                  <Col md="6">
                    <div id="delete-channels-task-summary">
                      <h5>Task Id: {cloudTaskId}</h5>
                      <h5>Client Name: {taskData?.client_name}</h5>
                      <h5>
                        Environment Name: {taskData?.client_environment_name}
                      </h5>
                      <h5>Status: {getTaskStatusLabel(taskData?.status, "deletechannels")}</h5>
                      <h5>Outcome: {getOutcomeLabel(taskData?.outcome)}</h5>
                      <h5>Message: {message}</h5>
                      <h5>Delete Job Id: {taskData?.delete_job_id}</h5>
                      <h5>Total Channels: {taskData?.total_channels}</h5>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            {reportData.length > 0 && (
                <ReactTable
                  id="delete-channels-task-report-table"
                  data={reportData}
                  totalCount={reportData.length}
                  makePaginationStatic={true}
                  hidePagination={true}
                  numberOfRowsData={[]}
                  columnMapping={columnMapping}
                  tableName="Delete Channels Report"
                  CSVFileName={"Delete_Channels_Report_Export"}
                  CSVHeaders={headers}
                  columns={[
                    {
                      Header: "Channel ID",
                      accessor: "channel_id",
                      filterable: false,
                      showTooltip: true,
                      sortable: true,
                    },
                    {
                      Header: "Channel Name",
                      accessor: "channel_name",
                      filterable: false,
                      showTooltip: true,
                      sortable: true,
                    },
                  ]}
                />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default DeleteChannelsTaskDetails;
